import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/realtime";
import { useAuth } from "@/context/AuthContext";
import Sidebar from "@/components/Sidebar";
import UploadZone from "@/components/UploadZone";
import TableView from "@/components/TableView";
import DatabaseOverview from "@/components/DatabaseOverview";
import DatabaseList from "@/components/DatabaseList";
import OfflineBanner from "@/components/OfflineBanner";
import { MENU, useUi } from "@/lib/uiSettings";

export default function Dashboard({ children }) {
  const { dbId, table } = useParams();
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [databases, setDatabases] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const [mainId, setMainId] = useState(null);
  const ui = useUi();
  const { can } = useAuth();
  useEffect(() => { api.get("/app-settings").then((r) => setMainId(r.data.main_db_id)).catch(() => {}); }, []);
  useEffect(() => {
    if (children || dbId || !ui.start_page || ui.start_page === "/" || sessionStorage.getItem("started")) return;
    const m = Object.values(MENU).find((x) => x.to === ui.start_page);
    sessionStorage.setItem("started", "1");
    if (m && (m.admin ? isAdmin : can(m.perm))) navigate(ui.start_page, { replace: true });
  }, [ui.start_page]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/databases");
      setDatabases(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener("databases-changed", load); return () => window.removeEventListener("databases-changed", load); }, [load]);
  useLiveRefresh(load);

  const current = databases.find((d) => d.id === dbId);
  const tableMeta = current?.tables.find((t) => t.name === table);

  const onUploaded = (d) => {
    setDatabases((prev) => [d, ...prev]);
    navigate(`/db/${d.id}`);
  };

  const onDeleted = (id) => {
    setDatabases((prev) => prev.filter((d) => d.id !== id));
    if (id === dbId) navigate("/");
  };

  let main;
  if (children) main = children;
  else if (dbId && table && tableMeta) main = <TableView key={`${dbId}-${table}`} db={current} table={tableMeta} onChange={load} databases={databases} onSubCreated={(d) => { setDatabases((p) => [d, ...p]); navigate(`/db/${d.id}`); }} />;
  else if (dbId && current) main = <DatabaseOverview db={current} mainId={mainId} onMainChange={setMainId} />;
  else if (dbId && loaded) main = <p className="text-muted-foreground" data-testid="not-found">Database or table not found.</p>;
  else if (!isAdmin) main = (
    <div className="max-w-2xl fade-up" data-testid="tech-home">
      <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Welcome{user?.name ? `, ${user.name}` : ""}</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Your databases</h1>
      <div className="mt-6 grid sm:grid-cols-2 gap-3">
        {databases.map((d) => (
          <Link key={d.id} to={`/db/${d.id}`} className="border rounded-lg bg-card p-5 hover:border-primary transition-colors" data-testid={`tech-db-card-${d.id}`}>
            <p className="font-display text-lg font-bold">{d.name}</p>
            <p className="text-xs text-muted-foreground mt-1">{d.tables.length} tables · {d.tables.reduce((s, t) => s + t.row_count, 0)} records</p>
          </Link>
        ))}
        {loaded && databases.length === 0 && <p className="text-muted-foreground" data-testid="tech-no-dbs">The admin hasn't given you any databases yet.</p>}
      </div>
    </div>
  );
  else main = <><UploadZone onUploaded={onUploaded} /><DatabaseList databases={databases} mainId={mainId} onDeleted={onDeleted} /></>;

  return (
    <div className="flex app-shell overflow-hidden">
      <Sidebar databases={databases} mainId={mainId} activeDb={dbId} activeTable={table} onDeleted={onDeleted} />
      <main className="flex-1 min-w-0 overflow-y-auto" data-testid="main-content">
        <OfflineBanner />
        <div className="p-5 md:p-8">{main}</div>
      </main>
    </div>
  );
}
